<template>
    <div
        class="advanced-billing-job"
        :style="{ '--job-timeline-color': timelineColor }"
    >
        <div class="billing-summary">
            <div class="billing-summary-field">
                <div class="billing-summary-label">
                    Billing codes
                </div>
                <v-tooltip
                    bottom
                    :disabled="billingCodeAssigned"
                >
                    <template #activator="{ on, attrs }">
                        <div
                            class="billing-summary-value"
                            v-bind="attrs"
                            v-on="on"
                        >
                            {{ billingCodeLabel }}
                        </div>
                    </template>
                    <span>
                        Billing codes are added in the Respond dialog after you
                        arrive at the ticket location.
                    </span>
                </v-tooltip>
            </div>
            <div class="billing-summary-field">
                <div class="billing-summary-label">
                    Rate periods
                </div>
                <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                        <div
                            class="rate-period-chips"
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-chip
                                v-for="ratePeriod in ratePeriodRows"
                                :key="ratePeriod.key"
                                :color="ratePeriodColor(ratePeriod.rateType)"
                                label
                                outlined
                                x-small
                            >
                                {{ ratePeriod.label }}
                            </v-chip>
                        </div>
                    </template>
                    <span>
                        Rate periods are assigned automatically from the billable
                        start through Complete, including captured travel before
                        Arrive, using the company's configured time ranges.
                    </span>
                </v-tooltip>
            </div>
        </div>

        <div class="job-summary">
            <div class="job-number">
                {{ job.ticketNumber || job.id }}
            </div>
            <div class="text-body-2 text--secondary mt-1">
                <v-icon
                    class="mr-1"
                    small
                >
                    mdi-map-marker-outline
                </v-icon>
                {{ job.address }}
            </div>
            <div class="job-status mt-3">
                <v-chip
                    :color="statusColor"
                    label
                    outlined
                    x-small
                >
                    {{ statusLabel }}
                </v-chip>
            </div>
        </div>

        <div class="job-actions">
            <v-btn
                v-if="job.status === 'pending'"
                color="primary"
                :disabled="!dayActive"
                depressed
                small
                @click="$emit('arrive', job.id)"
            >
                <v-icon
                    left
                    small
                >
                    mdi-check-circle-outline
                </v-icon>
                Arrive
            </v-btn>
            <v-tooltip
                v-else-if="job.status === 'in-progress'"
                bottom
                :disabled="canComplete"
            >
                <template #activator="{ on, attrs }">
                    <span
                        class="job-complete-control"
                        v-bind="attrs"
                        v-on="on"
                    >
                        <v-btn
                            color="primary"
                            :disabled="!canComplete"
                            depressed
                            small
                            @click="$emit('complete', job.id)"
                        >
                            <v-icon
                                left
                                small
                            >
                                mdi-check-circle
                            </v-icon>
                            Complete
                        </v-btn>
                    </span>
                </template>
                <span>
                    You must submit at least one response before completing
                    this job.
                </span>
            </v-tooltip>
            <v-tooltip
                v-if="canRemove"
                bottom
            >
                <template #activator="{ on, attrs }">
                    <v-btn
                        v-bind="attrs"
                        class="job-remove-button mt-1"
                        color="error"
                        text
                        x-small
                        v-on="on"
                        @click="$emit('cancel', job.id)"
                    >
                        Remove
                    </v-btn>
                </template>
                <span>Remove from Today's Jobs</span>
            </v-tooltip>
            <v-btn
                class="job-expand-button"
                icon
                small
                @click="expanded = !expanded"
            >
                <v-icon color="primary">
                    {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
            </v-btn>
        </div>

        <v-expand-transition>
            <div
                v-if="expanded"
                class="job-details"
            >
                <dl class="job-detail-list">
                    <dt>Status:</dt>
                    <dd>{{ statusLabel }}</dd>
                    <dt>Arrived:</dt>
                    <dd>{{ formatTime(job.arrivedAt) }}</dd>
                    <template v-if="job.billingStartedAt">
                        <dt>Billable from:</dt>
                        <dd>{{ formatTime(job.billingStartedAt) }}</dd>
                    </template>
                    <dt>Billing Code:</dt>
                    <dd>{{ billingCodeLabel }}</dd>
                    <dt>Location:</dt>
                    <dd>{{ job.address }}</dd>
                    <dt>Rate Periods:</dt>
                    <dd class="rate-period-details">
                        <div
                            v-for="ratePeriod in ratePeriodRows"
                            :key="ratePeriod.key"
                        >
                            <v-chip
                                :color="ratePeriodColor(ratePeriod.rateType)"
                                label
                                outlined
                                x-small
                            >
                                {{ ratePeriod.label }}
                            </v-chip>
                            <span v-if="ratePeriod.startedAt">
                                {{ formatRatePeriodRange(ratePeriod) }}
                            </span>
                        </div>
                    </dd>
                    <template v-if="allocationRows.length">
                        <dt>Allocations:</dt>
                        <dd>
                            <div
                                v-for="allocation in allocationRows"
                                :key="allocation.key"
                            >
                                {{ allocation.label }}
                            </div>
                        </dd>
                    </template>
                    <dt>Completed:</dt>
                    <dd>{{ formatTime(job.completedAt) }}</dd>
                </dl>
                <v-tooltip
                    bottom
                    :disabled="canRespond"
                >
                    <template #activator="{ on, attrs }">
                        <span
                            class="job-respond-control"
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-btn
                                class="job-respond-button"
                                color="primary"
                                :disabled="!canRespond"
                                depressed
                                @click="$emit('respond', job)"
                            >
                                <v-icon
                                    left
                                    small
                                >
                                    mdi-reply
                                </v-icon>
                                Respond
                            </v-btn>
                        </span>
                    </template>
                    <span>
                        You must arrive at the work location before responding.
                    </span>
                </v-tooltip>
            </div>
        </v-expand-transition>
    </div>
</template>

<script>
export default {
    name: 'AdvancedBillingJob',
    props: {
        dayActive: {
            type: Boolean,
            default: false,
        },
        job: {
            type: Object,
            required: true,
        },
        timelineColor: {
            type: String,
            default: '#42A5F5',
        },
    },
    data: () => ({
        expanded: false,
    }),
    computed: {
        allocationRows() {
            return Array.isArray(this.job.allocations)
                ? this.job.allocations.map((allocation, index) => ({
                    key: [
                        allocation.billingCodeId || allocation.billingCode,
                        allocation.ratePeriodId || allocation.rateLabel,
                        allocation.startedAt,
                        index,
                    ].join('-'),
                    label: this.formatAllocation(allocation),
                }))
                : [];
        },
        billingCodeLabel() {
            const billingCodes = Array.isArray(this.job.billingCodes)
                ? this.job.billingCodes
                    .map(billingCode => String(billingCode.code || '').trim())
                    .filter(Boolean)
                : [];
            const billingCode = billingCodes.join(', ')
                || String(this.job.billingCode || '').trim();
            return billingCode && billingCode.toLowerCase() !== 'not assigned'
                ? billingCode
                : 'Not assigned';
        },
        billingCodeAssigned() {
            return this.billingCodeLabel !== 'Not assigned';
        },
        canRespond() {
            return ['in-progress', 'completed'].includes(this.job.status);
        },
        canComplete() {
            return this.job.status === 'in-progress'
                && Boolean(this.job.respondedAt);
        },
        canRemove() {
            return ['pending', 'in-progress'].includes(this.job.status)
                || (!this.dayActive && this.job.status === 'cancelled');
        },
        ratePeriodLabel() {
            return [...new Set(this.ratePeriodRows.map(row => row.label))]
                .join(', ');
        },
        ratePeriodRows() {
            const rows = new Map();
            const allocations = Array.isArray(this.job.allocations)
                ? this.job.allocations
                : [];
            allocations.forEach(allocation => {
                const label = this.normalizeRatePeriodLabel(
                    allocation.rateLabel
                );
                const key = [
                    allocation.ratePeriodId || label,
                    allocation.startedAt || '',
                    allocation.endedAt || '',
                ].join('-');
                if(!rows.has(key)) {
                    rows.set(key, {
                        key,
                        label,
                        rateType: allocation.rateType || '',
                        startedAt: allocation.startedAt || null,
                        endedAt: allocation.endedAt || null,
                        seconds: Number(allocation.seconds) || 0,
                    });
                }
            });
            if(rows.size) {
                return [...rows.values()];
            }
            const label = this.normalizeRatePeriodLabel(this.job.rateLabel);
            return [{
                key: this.job.ratePeriodId || label,
                label,
                rateType: this.job.rateType || '',
                startedAt: null,
                endedAt: null,
                seconds: 0,
            }];
        },
        statusColor() {
            return {
                pending: 'grey',
                'in-progress': 'primary',
                completed: 'success',
                cancelled: 'error',
            }[this.job.status];
        },
        statusLabel() {
            return {
                pending: 'Yet to Start',
                'in-progress': 'In Progress',
                completed: 'Completed',
                cancelled: 'Cancelled',
            }[this.job.status];
        },
    },
    watch: {
        'job.status'(status, previousStatus) {
            if(status === 'in-progress' && previousStatus === 'pending') {
                this.expanded = true;
            }
        },
    },
    methods: {
        formatAllocation(allocation) {
            const parts = [
                allocation.billingCode || 'Not assigned',
                this.normalizeRatePeriodLabel(allocation.rateLabel),
            ];
            if(allocation.startedAt && allocation.endedAt) {
                parts.push(this.formatRatePeriodRange(allocation));
            }
            return parts.join(' · ');
        },
        formatDuration(seconds) {
            const totalMinutes = Math.max(
                0,
                Math.round((Number(seconds) || 0) / 60)
            );
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            if(hours && minutes) {
                return `${hours}h ${minutes}m`;
            }
            if(hours) {
                return `${hours}h`;
            }
            return `${minutes}m`;
        },
        formatRatePeriodRange(ratePeriod) {
            return `${this.formatTime(ratePeriod.startedAt)}–`
                + `${this.formatTime(ratePeriod.endedAt)} `
                + `(${this.formatDuration(ratePeriod.seconds)})`;
        },
        formatTime(value) {
            if(!value) {
                return '—';
            }
            const date = value instanceof Date ? value : new Date(value);
            if(!Number.isFinite(date.getTime())) {
                return '—';
            }
            return new Intl.DateTimeFormat('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }).format(date);
        },
        normalizeRatePeriodLabel(value) {
            const label = String(value || '').trim();
            return label
                && !['not assigned', 'rate not assigned'].includes(
                    label.toLowerCase()
                )
                ? label
                : 'Not assigned';
        },
        ratePeriodColor(rateType) {
            if(rateType === 'afterhours') {
                return 'warning';
            }
            return rateType === 'regular' ? 'primary' : 'grey';
        },
    },
};
</script>

<style scoped>
.advanced-billing-job {
    align-items: center;
    border-left: 4px solid var(--job-timeline-color);
    display: grid;
    column-gap: 22px;
    grid-template-columns: 148px minmax(0, 1fr) 116px;
    min-height: 112px;
    padding: 16px;
}

.advanced-billing-job + .advanced-billing-job {
    border-top: 1px solid rgba(128, 128, 128, .25);
}

.billing-summary {
    display: grid;
    gap: 10px;
    min-width: 0;
}

.billing-summary-field {
    min-width: 0;
}

.billing-summary-label {
    color: rgba(128, 128, 128, .9);
    font-size: .6875rem;
    font-weight: 500;
    letter-spacing: .04em;
    line-height: 1.2;
    text-transform: uppercase;
}

.billing-summary-value {
    font-size: .9375rem;
    font-weight: 500;
    margin-top: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.rate-period-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 3px;
}

.rate-period-details {
    display: grid;
    gap: 5px;
}

.rate-period-details > div {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.job-number {
    font-size: 1rem;
    font-weight: 500;
}

.job-details {
    align-items: start;
    background: rgba(128, 128, 128, .09);
    border-radius: 4px;
    display: grid;
    gap: 24px;
    grid-column: 1 / -1;
    grid-template-columns: minmax(0, 1fr) auto;
    margin-top: 16px;
    padding: 16px 20px;
}

.job-detail-list {
    display: grid;
    font-size: .8rem;
    gap: 8px 16px;
    grid-template-columns: 104px minmax(0, 1fr);
    margin: 0;
    max-width: 520px;
}

.job-detail-list dt {
    font-weight: 500;
}

.job-detail-list dd {
    margin: 0;
}

.job-respond-button {
    min-width: 104px;
}

.job-respond-control {
    display: inline-flex;
}

.job-actions {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

.job-expand-button {
    margin-top: 2px;
}

@media (max-width: 760px) {
    .advanced-billing-job {
        grid-template-columns: 88px minmax(0, 1fr);
        padding: 16px;
    }

    .job-actions {
        grid-column: 1 / -1;
        flex-direction: row;
        justify-content: flex-start;
    }

    .job-details {
        grid-template-columns: 1fr;
    }

    .job-respond-button {
        justify-self: start;
    }
}
</style>
